/**
 * FAQ Section
 * Frequently asked questions with accordion
 */

import { motion } from 'framer-motion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';
import { staggerContainerVariants, itemVariants } from '@/components/animations/variants';

interface FAQ {
  question: string;
  answer: string;
  link?: {
    text: string;
    href: string;
  };
}

const faqs: FAQ[] = [
  {
    question: 'How does Pathfinder save money?',
    answer:
      'Pathfinder analyzes each prompt and routes it to the most cost-effective model that can handle the task. Simple questions go to fast, cheap models (e.g., Gemini Flash-Lite at $0.15/1M tokens), while complex tasks use premium models (e.g., Claude Sonnet at $3.00/1M). This intelligent routing saves 40-70% vs. always using expensive models.',
    link: {
      text: 'See cost breakdown',
      href: '/roi-calculator',
    },
  },
  {
    question: 'Do you store my prompts?',
    answer:
      'No. Pathfinder never stores raw prompts—only SHA-256 hashes and extracted features (length, complexity, task type). This is enforced at the database schema level and validated in CI. Your data never leaves your control. We are GDPR compliant by design.',
    link: {
      text: 'Read privacy policy',
      href: '/security',
    },
  },
  {
    question: 'How accurate is the routing?',
    answer:
      'Our ML-powered routing engine achieves 94% accuracy in selecting the optimal model. It continuously learns from feedback signals (latency, cost, quality) and improves over time. You can also override routing decisions with custom policies or use Shadow Mode to validate routing before going live.',
    link: {
      text: 'View routing docs',
      href: '/docs/routing',
    },
  },
  {
    question: 'Can I use my own API keys?',
    answer:
      'Yes! Pathfinder supports bring-your-own-key (BYOK) for all providers (Anthropic, OpenAI, Google). You pay providers directly at their standard rates, plus a small routing fee ($0.001 per request). This gives you full cost transparency and control.',
  },
  {
    question: "What's the latency overhead?",
    answer:
      'Routing decisions happen in <50ms (p95) with caching enabled. Total latency is typically <150ms including model selection, feature extraction, and policy evaluation. This is negligible compared to model inference time (1-5 seconds).',
    link: {
      text: 'View performance benchmarks',
      href: '/docs/performance',
    },
  },
  {
    question: 'How do I get started?',
    answer:
      'Sign up for a free account (10,000 requests/month). Add your API keys for Anthropic, OpenAI, and Google. Replace your OpenAI SDK endpoint with Pathfinder—no code changes needed. Start routing immediately. See our 5-minute quickstart guide.',
    link: {
      text: 'View quickstart',
      href: '/docs/quickstart',
    },
  },
  {
    question: 'Do you support fine-tuned models?',
    answer:
      'Yes. You can add custom fine-tuned models from any provider and include them in routing policies. Pathfinder treats them like any other model—evaluating cost, latency, and capability for each request.',
  },
  {
    question: 'Can I customize routing logic?',
    answer:
      'Absolutely. Pathfinder supports custom routing policies in Python or JSON. Define rules based on prompt features, user metadata, time of day, budget constraints, or any other criteria. You can also use our visual policy builder.',
    link: {
      text: 'Explore policies',
      href: '/docs/policies',
    },
  },
  {
    question: 'What happens if a provider is down?',
    answer:
      'Pathfinder automatically fails over to backup providers in <2 seconds. Our uptime dashboard shows real-time provider health. You can also configure fallback chains (e.g., Claude → GPT → Gemini) to ensure 99.9% uptime.',
    link: {
      text: 'View uptime status',
      href: '/provider-uptime',
    },
  },
  {
    question: 'How does billing work?',
    answer:
      'You pay two components: (1) Direct API costs to providers (at their standard rates), and (2) a 5.5% platform fee based on usage. All usage is shown in real-time on your dashboard.',
    link: {
      text: 'See pricing details',
      href: '#pricing',
    },
  },
  {
    question: 'Is there a free trial?',
    answer:
      'Spark includes 50,000 requests per month. Billing is required for usage-based charges on paid plans.',
  },
  {
    question: "What's your SLA?",
    answer:
      'Professional plans include 99.9% uptime SLA (43 minutes/month downtime). Enterprise plans include 99.95% SLA with dedicated support and phone escalation. We also offer custom SLAs with financial penalties for mission-critical workloads.',
    link: {
      text: 'View SLA terms',
      href: '/sla',
    },
  },
];

export function FaqSection() {
  return (
    <section className="py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to know about Pathfinder
          </p>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <motion.div key={index} variants={itemVariants}>
                <AccordionItem value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-lg font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3">
                      <p className="text-muted-foreground leading-relaxed">{faq.answer}</p>
                      {faq.link && (
                        <Button variant="link" className="p-0 h-auto" asChild>
                          <a
                            href={faq.link.href}
                            className="inline-flex items-center space-x-1 text-accent"
                          >
                            <span>{faq.link.text}</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-muted-foreground mb-4">Still have questions?</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button variant="outline" asChild>
              <a href="https://docs.naivana.io/" target="_blank" rel="noopener noreferrer">
                View Documentation
              </a>
            </Button>
            <Button>Talk to Sales</Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
